import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { FxAccentId } from "./accents";
import {
  applyFxTheme,
  persistFxAccent,
  persistFxTheme,
  playFxThemeFade,
  readFxAccent,
  readFxTheme,
  type FxTheme,
} from "./theme";

type ThemeCtx = {
  theme: FxTheme;
  accent: FxAccentId;
  setTheme: (t: FxTheme) => void;
  setAccent: (a: FxAccentId) => void;
};

const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<FxTheme>(() => readFxTheme());
  const [accent, setAccentState] = useState<FxAccentId>(() => readFxAccent());

  const setTheme = useCallback(
    (t: FxTheme) => {
      setThemeState(t);
      persistFxTheme(t);
      applyFxTheme(t, accent);
      playFxThemeFade();
    },
    [accent]
  );

  const setAccent = useCallback(
    (a: FxAccentId) => {
      setAccentState(a);
      persistFxAccent(a);
      applyFxTheme(theme, a);
    },
    [theme]
  );

  const value = useMemo(() => ({ theme, accent, setTheme, setAccent }), [theme, accent, setTheme, setAccent]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFxTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useFxTheme");
  return ctx;
}
